<template>
  <main class="main">
    <div class="buttons">
      <router-link :to="{ name: 'equipement-creation' }" custom v-slot="{ navigate }">
        <ButtonElement color="bordeaux" :icon="PhPlus" @click="navigate">
          Ajouter un équipement
        </ButtonElement>
      </router-link>
    </div>
    <div class="cards" v-if="equipements.length">
      <CardComponent
        v-for="equipement in equipements"
        :key="equipement.id"
        :transparent="!equipement.actif"
        :icon="PhTruck"
        :title="`${equipement.nom}${!equipement.actif ? ' (supprimé)' : ''}`"
      >
        <template #buttons>
          <router-link
            :to="{ name: 'equipement-modification', params: { id: equipement.id } }"
            custom
            v-slot="{ navigate }"
          >
            <ButtonElement
              v-if="equipement.actif"
              color="bordeaux"
              :icon="PhPen"
              size="small"
              @click="navigate"
              >Modifier</ButtonElement
            >
          </router-link>
          <ButtonElement
            color="danger"
            bordered
            :icon="equipement.actif ? PhTrash : PhRecycle"
            :disabled="!cacheStore.online"
            size="small"
            label-class="tablet-only"
            @click="confirmDeleteEquipement(equipement)"
            >{{ equipement.actif ? 'Supprimer' : 'Restaurer' }}</ButtonElement
          >
        </template>
        <CardInfoComponent title="Catégorie">{{ equipement.categorie_nom }}</CardInfoComponent>
      </CardComponent>
    </div>
    <div class="message" v-else>
      <PhWarning />
      <span>Aucun équipement n'est enregistré.</span>
    </div>
  </main>
  <ConfirmElement ref="confirmRef" @yes="deleteEquipement" />
</template>

<script setup lang="ts">
import { PhPen, PhPlus, PhRecycle, PhTrash, PhTruck, PhWarning } from '@phosphor-icons/vue'
import ButtonElement from '@/components/ButtonElement.vue'
import useFetch from '@/composables/useFetch.ts'
import { onMounted, ref, type Ref, useTemplateRef } from 'vue'
import type EquipementList from '@/interfaces/Equipement.ts'
import { useLoadingStore } from '@/stores/loading.ts'
import ConfirmElement from '@/components/ConfirmElement.vue'
import CardComponent from '@/components/card/CardComponent.vue'
import CardInfoComponent from '@/components/card/CardInfoComponent.vue'
import { useCacheStore } from '@/stores/cache.ts'

// dépendances
const api = useFetch()

// stores
const cacheStore = useCacheStore()
const loadingStore = useLoadingStore()

const equipements: Ref<EquipementList[]> = ref([])

const confirmRef = useTemplateRef<typeof ConfirmElement>('confirmRef')

async function getEquipements() {
  equipements.value = await cacheStore.getEquipements()
}

function confirmDeleteEquipement(equipement: EquipementList) {
  confirmRef.value?.show(
    equipement,
    `${equipement.actif ? 'Suppression' : 'Restauration'} d\'un équipement`,
    `Êtes-vous sûr de vouloir ${equipement.actif ? 'supprimer' : 'restaurer'} l'équipement <b class="b">${equipement.nom}</b> ?`,
  )
}

async function deleteEquipement(equipement: EquipementList) {
  loadingStore.start()
  try {
    await api.delete(`equipements/${equipement.id}`)
    await getEquipements()
  } finally {
    loadingStore.stop()
  }
}

onMounted(async () => {
  loadingStore.start()
  try {
    await getEquipements()
  } finally {
    loadingStore.stop()
  }
})
</script>
